import { Card, Col, List, Row, Skeleton, Tag, Typography, theme as antdTheme } from 'antd';
import { NotificationOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import dayjs from 'dayjs';
import { KpiCard, type KpiTone } from '@/shared/components/ui';
import { EmptyState } from '@/shared/components/ui/empty-state';
import type { DashboardSummary } from '@/api/dashboard';

/** Shared pieces for the per-role dashboards. Keep these dumb — no API calls,
 *  no decisions about which role sees what; just rendering primitives. */

export const PKR = (n: number) =>
  new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', maximumFractionDigits: 0 }).format(n);

export type Tone = KpiTone;

export interface KpiSpec {
  key: string;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  /** When true, value + hint render through `<MaskedAmount>` (eye toggle). */
  sensitive?: boolean;
  icon: ReactNode;
  tone?: Tone;
}

export function DashboardHeader({
  role,
  asOf,
  title = 'Dashboard',
}: { role: string | null; asOf: string; title?: string }) {
  return (
    <header>
      <Typography.Text type="secondary" style={{ fontSize: 12, letterSpacing: 0.6, textTransform: 'uppercase' }}>
        {role ? `Signed in as ${role.replace(/_/g, ' ').toLowerCase()}` : 'Overview'}
      </Typography.Text>
      <Typography.Title level={2} style={{ margin: '4px 0 0', fontSize: 28 }}>
        {title}
      </Typography.Title>
      <Typography.Text type="secondary">
        Snapshot for {dayjs(asOf).format('dddd, D MMMM YYYY')}
      </Typography.Text>
    </header>
  );
}

export function KpiGrid({ kpis }: { kpis: KpiSpec[] }) {
  const cols = kpis.length <= 2 ? 12 : kpis.length === 3 ? 8 : 6;
  return (
    <Row gutter={[16, 16]}>
      {kpis.map((k) => (
        <Col key={k.key} xs={24} sm={12} xl={cols}>
          <KpiCard
            label={k.label}
            value={k.value}
            hint={k.hint}
            icon={k.icon}
            tone={k.tone}
            sensitive={k.sensitive}
          />
        </Col>
      ))}
    </Row>
  );
}

export function SignalRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  const { token } = antdTheme.useToken();
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 0',
      borderBottom: `1px solid ${token.colorBorderSecondary}`,
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: token.colorTextSecondary }}>
        <span style={{
          width: 28, height: 28, borderRadius: 7,
          display: 'grid', placeItems: 'center',
          background: token.colorFillTertiary, color: token.colorTextSecondary,
        }}>{icon}</span>
        <span style={{ fontSize: 13 }}>{label}</span>
      </span>
      <Typography.Text strong>{value}</Typography.Text>
    </div>
  );
}

export function RecentNoticesCard({ notices }: { notices: DashboardSummary['recentNotices'] }) {
  return (
    <Card title="Recent notices">
      {notices.length === 0 ? (
        <EmptyState
          icon={<NotificationOutlined />}
          title="No notices yet"
          description="School-wide announcements will appear here once they're posted."
        />
      ) : (
        <List
          dataSource={notices}
          renderItem={(n) => (
            <List.Item>
              <List.Item.Meta
                title={<Typography.Text strong>{n.title}</Typography.Text>}
                description={<Tag>{n.category}</Tag>}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(n.postedAt).format('D MMM, HH:mm')}
              </Typography.Text>
            </List.Item>
          )}
        />
      )}
    </Card>
  );
}

export function DashboardSkeleton() {
  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <Skeleton.Input active style={{ width: 220, height: 36 }} />
      <Row gutter={[16, 16]}>
        {[0, 1, 2, 3].map((i) => (
          <Col key={i} xs={24} sm={12} xl={6}>
            <Card><Skeleton active paragraph={{ rows: 2 }} /></Card>
          </Col>
        ))}
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}><Card><Skeleton active paragraph={{ rows: 6 }} /></Card></Col>
        <Col xs={24} xl={8}><Card><Skeleton active paragraph={{ rows: 6 }} /></Card></Col>
      </Row>
    </div>
  );
}
